import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { resolveCentralOrigin } from 'shared/api/apiOrigin';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { sessionHasPermission } from 'shared/utils/sessionPermissions';
import { softwareApi } from '../../api/systemRegisterApi';
import { MONITORING_TYPE, formatEnumLabel } from '../../constants/enums';
import DeploymentStageApprovalDialog from '../../shared/DeploymentStageApprovalDialog';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
  reportKeepTogetherSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'monitoring-review-form';
const PRINTING_BODY_CLASS = 'sr-printing-monitoring-form';

const FIRST_REVIEW_APPROVAL_PERMISSION = 'PERFORM_FIRST_REVIEW_APPROVAL';
const SECOND_REVIEW_APPROVAL_PERMISSION = 'PERFORM_SECOND_REVIEW_APPROVAL';

function isAdminRole(user) {
  const role = user?.data?.role;
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
  return normalized === 'ADMIN';
}

function fileHref(location) {
  if (!location) return null;
  if (location.startsWith('http://') || location.startsWith('https://')) return location;
  return `${resolveCentralOrigin()}${location.startsWith('/') ? '' : '/'}${location}`;
}

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

const WIZARD_SNAPSHOT_KEY = 'sr-register-wizard-snapshot';

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

function approvalDecisionLabel(approval) {
  if (!approval?.approvalStatus) return null;
  if (approval.approvalStatus === 'APPROVED') return 'APPROVED';
  if (approval.approvalStatus === 'REJECTED') return 'DECLINED';
  return formatEnumLabel(approval.approvalStatus);
}

/** Pull the free-text note from stored approvalComments (`… | Comment: …`). */
function extractApprovalComment(approvalComments) {
  if (!approvalComments?.trim()) return '';
  const match = String(approvalComments).match(/\|\s*Comment:\s*([\s\S]+)$/i);
  if (match) return match[1].trim();
  return String(approvalComments).trim();
}

function getDeclinedComments(approvals) {
  return (approvals ?? [])
    .filter((a) => a?.approvalStatus === 'REJECTED')
    .map((a) => ({
      role: a.approverRole || 'Approver',
      comment: extractApprovalComment(a.approvalComments),
      approverName: a.approverName || '',
    }))
    .filter((row) => row.comment);
}

function patchWizardSnapshotReview(updatedReview) {
  try {
    const raw = sessionStorage.getItem(WIZARD_SNAPSHOT_KEY);
    if (!raw || !updatedReview) return;
    const snap = JSON.parse(raw);
    const entries = snap?.wizardData?.monitoringReview?.entries;
    if (!Array.isArray(entries)) return;
    const next = entries.map((e) => {
      const sameKey = updatedReview.key && e.key === updatedReview.key;
      const sameId = updatedReview.id != null && e.id === updatedReview.id;
      return sameKey || sameId ? { ...e, ...updatedReview } : e;
    });
    snap.wizardData.monitoringReview.entries = next;
    sessionStorage.setItem(WIZARD_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    // ignore snapshot sync failures
  }
}

function extractReviewFromSoftwareDetail(detail, reviewId, reviewKey) {
  const list = detail?.monitoringReviews ?? [];
  const matched =
    list.find(
      (r) =>
        reviewId != null
        && (r.monitoringReviewId === reviewId || r.id === reviewId)
    )
    || list.find((r) => reviewKey && String(r.key) === String(reviewKey));
  if (!matched) return null;
  return {
    id: matched.monitoringReviewId ?? matched.id ?? null,
    linkId: matched.linkId ?? null,
    monitoringReviewId: matched.monitoringReviewId ?? matched.id ?? null,
    monitoringType: matched.monitoringType ?? '',
    reviewAreaControl: matched.reviewAreaControl ?? '',
    standardControlReference: matched.standardControlReference ?? '',
    reviewFrequency: matched.reviewFrequency ?? '',
    observationFindings: matched.observationFindings ?? '',
    riskRating: matched.riskRating ?? '',
    correctiveActionRequired: matched.correctiveActionRequired ?? '',
    actionOwnerEmail: matched.actionOwnerEmail ?? '',
    status: matched.status ?? 'OPEN',
    nextReviewDate: matched.nextReviewDate ?? '',
    evidenceFileName: matched.evidenceFileName ?? '',
    evidenceContentType: matched.evidenceContentType ?? '',
    evidenceLocation: matched.evidenceLocation ?? '',
    reviewer: matched.reviewer ?? '',
    approvals: (matched.approvals ?? []).map((a) => ({
      id: a.id ?? null,
      monitoringReviewId: a.monitoringReviewId ?? matched.monitoringReviewId ?? matched.id ?? null,
      approverName: a.approverName ?? '',
      approverRole: a.approverRole ?? '',
      approverCommittee: a.approverCommittee ?? '',
      approvalDate: a.approvalDate ?? '',
      approvalStatus: a.approvalStatus ?? '',
      approvalComments: a.approvalComments ?? '',
      approvalLevel: a.approvalLevel ?? '',
      signatureConfirmation: a.signatureConfirmation ?? '',
    })),
  };
}

/**
 * Paper-style Monitoring & Review form view (tenant logo + structured tables).
 */
export default function MonitoringReviewViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const querySoftwareId = searchParams.get('softwareId');
  const queryReviewId = searchParams.get('id');

  const [softwareName, setSoftwareName] = useState(location.state?.softwareName || '');
  const [softwareId, setSoftwareId] = useState(location.state?.softwareId ?? null);

  const [review, setReview] = useState(() => location.state?.review || null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSlot, setDialogSlot] = useState(1);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (location.state?.review) {
      setReview(location.state.review);
      setSoftwareName(location.state.softwareName || '');
      setSoftwareId(location.state.softwareId ?? null);
      setLoadError('');
      return;
    }

    if (!querySoftwareId || !queryReviewId) {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await softwareApi.get(querySoftwareId);
        const detail = unwrapData(response);
        if (cancelled) return;
        const fromApi = extractReviewFromSoftwareDetail(
          detail,
          Number(queryReviewId),
          null
        );
        if (!fromApi) {
          setReview(null);
          setLoadError('Monitoring review not found for this software.');
          return;
        }
        setReview(fromApi);
        setSoftwareName(detail?.softwareName || '');
        setSoftwareId(detail?.id ?? Number(querySoftwareId));
      } catch (error) {
        if (!cancelled) {
          setReview(null);
          setLoadError(apiErrorMessage(error, 'Failed to load monitoring review details.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [location.state, querySoftwareId, queryReviewId]);

  const approvals = review?.approvals ?? [];
  const firstApproval = findSlotApproval(approvals, 1);
  const secondApproval = findSlotApproval(approvals, 2);
  const declinedComments = getDeclinedComments(approvals);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const isAdmin = isAdminRole(user);
  const canFirstApproval =
    isAdmin || sessionHasPermission(profile, FIRST_REVIEW_APPROVAL_PERMISSION);
  const canSecondApproval =
    isAdmin || sessionHasPermission(profile, SECOND_REVIEW_APPROVAL_PERMISSION);
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [
      isDark,
      tenantBranding?.backgroundColor,
      tenantBranding?.primaryColor,
      tenantBranding?.secondaryColor,
    ]
  );
  const tenantPhoto = tenantBranding?.tenantPhoto;
  const tenantLogoSrc = useProfilePhotoSrc(tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const currentUser = useMemo(
    () => ({
      name: profile?.name?.trim() || '',
      email: profile?.email?.trim() || '',
    }),
    [profile?.name, profile?.email]
  );

  const evidenceHref = useMemo(
    () => fileHref(review?.evidenceLocation),
    [review?.evidenceLocation]
  );

  const discussedText = useMemo(() => {
    if (!review) return '';
    return [
      dash(review.observationFindings),
      review.correctiveActionRequired
        ? `Corrective action: ${dash(review.correctiveActionRequired)}`
        : '',
      review.riskRating ? `Risk rating: ${formatEnumLabel(review.riskRating)}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
  }, [review]);

  const applyLocalApproval = useCallback(
    ({ slot, decision, comment }) => {
      const role = slotRole(slot);
      const declined = decision === 'DECLINED';
      const today = new Date().toISOString().slice(0, 10);
      const displayName = currentUser.name || currentUser.email || 'Approver';
      const email = currentUser.email || '';
      const nextApproval = {
        id: findSlotApproval(review?.approvals, slot)?.id ?? null,
        monitoringReviewId: review?.monitoringReviewId ?? review?.id ?? null,
        approverName: displayName,
        approverRole: role,
        approverCommittee: '',
        approvalDate: today,
        approvalStatus: declined ? 'REJECTED' : 'APPROVED',
        approvalComments:
          `${declined ? 'Declined' : 'Approved'} monitoring review`
          + (email ? ` | Approver email: ${email}` : '')
          + (comment ? ` | Comment: ${comment}` : ''),
        approvalLevel: slot === 1 ? 'TECHNICAL' : 'BUSINESS',
        signatureConfirmation: email,
      };

      const withoutSlot = (review?.approvals ?? []).filter((a) => a?.approverRole !== role);
      const nextApprovals = [...withoutSlot, nextApproval];
      const anyDeclined = nextApprovals.some((a) => a.approvalStatus === 'REJECTED');
      const anyApproved = nextApprovals.some((a) => a.approvalStatus === 'APPROVED');
      const nextStatus = anyDeclined ? 'IN_PROGRESS' : anyApproved ? 'COMPLETED' : review?.status;

      const updated = {
        ...review,
        status: nextStatus,
        approvals: nextApprovals,
      };
      setReview(updated);
      patchWizardSnapshotReview(updated);
      return updated;
    },
    [currentUser.email, currentUser.name, review]
  );

  const openApprovalDialog = (slot) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) {
      toast.error('You do not have permission for this approval.');
      return;
    }
    if (!currentUser.name && !currentUser.email) {
      toast.error('Logged-in user name or email is required to approve or decline.');
      return;
    }
    setDialogSlot(slot);
    setDialogOpen(true);
  };

  const handleApprovalSubmit = async ({ decision, comment }) => {
    setSaving(true);
    try {
      const masterId = review?.monitoringReviewId ?? review?.id;
      if (softwareId && masterId) {
        const response = await softwareApi.recordMonitoringReviewApproval(softwareId, masterId, {
          slot: dialogSlot,
          name: currentUser.name || currentUser.email,
          email: currentUser.email || '',
          approvalDate: new Date().toISOString().slice(0, 10),
          decision,
          comment,
        });
        const detail = unwrapData(response);
        const fromApi = extractReviewFromSoftwareDetail(detail, masterId, review.key);
        if (fromApi) {
          const updated = { ...fromApi, key: review.key };
          setReview(updated);
          patchWizardSnapshotReview(updated);
        } else {
          applyLocalApproval({ slot: dialogSlot, decision, comment });
        }
      } else {
        applyLocalApproval({ slot: dialogSlot, decision, comment });
      }
      toast.success(
        decision === 'DECLINED'
          ? `${slotRole(dialogSlot)} declined.`
          : `${slotRole(dialogSlot)} approved.`
      );
      setDialogOpen(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save monitoring review approval.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !review || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const labelBase = (review.reviewAreaControl || softwareName || 'monitoring-review')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'monitoring-review';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-MONITORING-REVIEW-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, review, softwareName]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-monitoring-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 12mm !important;
          box-sizing: border-box !important;
          box-shadow: none !important;
          overflow: visible !important;
          border: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!review) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Monitoring review" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {loadError
              || 'Open a monitoring review from the Monitoring & Review step using the view action.'}
          </Typography>
          <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  const renderApprovalButton = (slot, approval) => {
    const allowed = slot === 1 ? canFirstApproval : canSecondApproval;
    if (!allowed) return null;

    const decision = approvalDecisionLabel(approval);
    const label = slotRole(slot);
    return (
      <Button
        key={slot}
        variant={decision === 'APPROVED' ? 'contained' : 'outlined'}
        color={decision === 'DECLINED' ? 'error' : decision === 'APPROVED' ? 'success' : 'secondary'}
        onClick={() => openApprovalDialog(slot)}
        disabled={saving}
        sx={{ textTransform: 'none' }}
      >
        {decision ? `${label} (${decision})` : label}
      </Button>
    );
  };

  const renderSignatureBlock = (approval, fallbackReviewer) => {
    if (approval) {
      const decision = approvalDecisionLabel(approval);
      const comment = extractApprovalComment(approval.approvalComments);
      const declined = decision === 'DECLINED';
      return (
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, mb: 0.35 }}>
            {approval.approverRole || 'Approver'}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'inherit' }}>
            {approval.approverName || '—'}
          </Typography>
          {approval.signatureConfirmation ? (
            <Typography sx={{ fontSize: '0.75rem', color: 'inherit', opacity: 0.85 }}>
              {approval.signatureConfirmation}
            </Typography>
          ) : null}
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: declined ? 'error.main' : 'inherit',
              fontWeight: declined ? 700 : 400,
              mt: 0.35,
            }}
          >
            {decision || '—'}
            {approval.approvalDate ? ` — ${approval.approvalDate}` : ''}
          </Typography>
          {declined && comment ? (
            <Box
              mt={0.6}
              sx={{
                borderLeft: '3px solid',
                borderColor: 'error.main',
                pl: 1,
                py: 0.25,
              }}
            >
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'error.main' }}>
                For implementation
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  color: 'inherit',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.35,
                }}
              >
                {comment}
              </Typography>
            </Box>
          ) : comment ? (
            <Typography sx={{ fontSize: '0.72rem', color: 'inherit', opacity: 0.8, mt: 0.35 }}>
              {comment}
            </Typography>
          ) : null}
        </Box>
      );
    }
    if (fallbackReviewer) {
      return (
        <Typography sx={{ fontSize: '0.82rem', color: 'inherit' }}>
          Reviewer: {fallbackReviewer}
        </Typography>
      );
    }
    return '—';
  };

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header
          title="Monitoring & Review Form"
        />
      </Box>

      <Box
        className="no-print"
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        gap={1}
        mb={2}
      >
        <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {renderApprovalButton(1, firstApproval)}
            {renderApprovalButton(2, secondApproval)}
            <Button
              type="button"
              startIcon={<PrintOutlinedIcon />}
              onClick={handlePrint}
              variant="outlined"
            >
              Print
            </Button>
            <Button
              type="button"
              startIcon={
                downloadingPdf ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PictureAsPdfOutlinedIcon />
                )
              }
              onClick={handleDownloadPdf}
              variant="contained"
              color="secondary"
              disabled={downloadingPdf}
            >
              {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
            </Button>
          </Box>
        </Box>

        {declinedComments.length > 0 ? (
          <Box
            sx={{
              alignSelf: 'stretch',
              maxWidth: 980,
              mx: 'auto',
              width: '100%',
              border: '1px solid',
              borderColor: 'error.light',
              bgcolor: (t) => alpha(t.palette.error.main, isDark ? 0.16 : 0.06),
              borderRadius: 1,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="subtitle2" color="error.main" fontWeight={800} mb={0.5}>
              Declined — comments for implementation
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Address the comments below, then request approval again.
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {declinedComments.map((item) => (
                <Box key={item.role}>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    {item.role}
                    {item.approverName ? ` · ${item.approverName}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {item.comment}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}` : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        {/* Banner header — mirrors paper form */}
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>

          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>

          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Monitoring and Review Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR MR1
            </Typography>
          </Box>
        </Box>
        {/* Upper identity / selection fields */}
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Software / System Name:" border={form.border} muted={form.muted} sx={{ width: '34%' }}>
                {dash(softwareName) || '—'}
              </FieldCell>
              <FieldCell label="Reviewer:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {dash(review.reviewer) || '—'}
              </FieldCell>
              <FieldCell label="Status:" border={form.border} muted={form.muted} sx={{ width: '33%' }}>
                {formatEnumLabel(review.status) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Monitoring Type (Please Select):" border={form.border} muted={form.muted} colSpan={3}>
                <Box display="flex" flexWrap="wrap" gap={{ xs: 0.5, sm: 2 }}>
                  {MONITORING_TYPE.map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          size="small"
                          checked={review.monitoringType === type}
                          readOnly
                          disableRipple
                          tabIndex={-1}
                          sx={{
                            py: 0.25,
                            color: form.checkbox || 'inherit',
                            '&.Mui-checked': { color: form.checkbox || 'primary.main' },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>
                          {formatEnumLabel(type)}
                        </Typography>
                      }
                      sx={{ mr: 1, ml: 0, color: 'inherit' }}
                    />
                  ))}
                </Box>
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Review Frequency:" border={form.border} muted={form.muted} sx={{ width: '50%' }} colSpan={2}>
                {formatEnumLabel(review.reviewFrequency) || '—'}
              </FieldCell>
              <FieldCell label="Next Review Date:" border={form.border} muted={form.muted}>
                {dash(review.nextReviewDate) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Action Owner:" border={form.border} muted={form.muted} sx={{ width: '50%' }} colSpan={2}>
                {dash(review.actionOwnerEmail) || '—'}
              </FieldCell>
              <FieldCell label="Risk Rating:" border={form.border} muted={form.muted}>
                {formatEnumLabel(review.riskRating) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Review Area / Control:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(review.reviewAreaControl) || '—'}
              </FieldCell>
            </TableRow>

            <TableRow>
              <FieldCell label="Standard / Control Reference:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(review.standardControlReference) || '—'}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box px={1.5} py={1.25}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.4, color: 'inherit' }}>
            Instructions:
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.45, color: form.muted }}>
            This form is used to record monitoring and review activity for the registered software
            system over the review period.
          </Typography>
        </Box>

        {/* Main monitoring log table */}
        <Box data-pdf-keep="true" sx={reportKeepTogetherSx}>
        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableHead>
            <TableRow>
              {[
                { label: 'Date', width: '12%' },
                { label: 'Item(s) discussed and proposed work plan till next meeting', width: '46%' },
                { label: 'Next Meeting', width: '14%' },
                { label: 'Reviewer / Approver signature', width: '28%' },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    border: form.border,
                    fontWeight: 800,
                    fontSize: '0.78rem',
                    bgcolor: form.headBg,
                    color: 'inherit',
                    width: col.width,
                    py: 1,
                    '@media print': {
                      bgcolor: '#f5f5f5 !important',
                      color: '#111 !important',
                      border: '1px solid #1a1a1a !important',
                    },
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  height: 130,
                  fontSize: '0.85rem',
                  color: 'inherit',
                }}
              >
                {dash(review.nextReviewDate) || '—'}
              </TableCell>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                  color: 'inherit',
                }}
              >
                {discussedText || '—'}
              </TableCell>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  fontSize: '0.85rem',
                  color: 'inherit',
                }}
              >
                {dash(review.nextReviewDate) || '—'}
              </TableCell>
              <TableCell
                sx={{
                  border: form.border,
                  verticalAlign: 'top',
                  fontSize: '0.82rem',
                  color: 'inherit',
                }}
              >
                <Box display="flex" flexDirection="column" gap={1.25}>
                  {firstApproval || secondApproval ? (
                    <>
                      {renderSignatureBlock(firstApproval)}
                      {renderSignatureBlock(secondApproval)}
                    </>
                  ) : (
                    renderSignatureBlock(null, review.reviewer)
                  )}
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: form.border, height: 88 }}>&nbsp;</TableCell>
              <TableCell sx={{ border: form.border }}>&nbsp;</TableCell>
              <TableCell sx={{ border: form.border }}>&nbsp;</TableCell>
              <TableCell sx={{ border: form.border }}>&nbsp;</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {review.evidenceFileName || evidenceHref ? (
          <Box px={1.5} py={1.25} sx={{ borderTop: form.border }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.4, color: 'inherit' }}>
              Evidence:
            </Typography>
            {evidenceHref ? (
              <Link
                href={evidenceHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '0.85rem',
                  wordBreak: 'break-all',
                  color: isDark ? '#9EC1FF' : 'primary.main',
                }}
              >
                {review.evidenceFileName || evidenceHref}
              </Link>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: 'inherit' }}>{review.evidenceFileName}</Typography>
            )}
          </Box>
        ) : null}

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this form with the system register records for audit and compliance.
        </Box>
        </Box>
      </Paper>

      <DeploymentStageApprovalDialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        onSubmit={handleApprovalSubmit}
        stageStatus="MONITORING_REVIEW"
        slot={dialogSlot}
        currentUser={currentUser}
        saving={saving}
        existingComment=""
        title={`${slotRole(dialogSlot)} — Monitoring Review`}
        description={
          <>
            Approve this monitoring and review form, or decline it and leave a comment. Your logged-in
            name and email will be written into the signature section. On approve, the review status
            becomes <strong>Completed</strong>.
          </>
        }
      />
    </Box>
  );
}
